import { inject, Injectable } from '@angular/core';
import { QualityTestStore } from '../stores/quality-test.store';
import { BandwidthService } from './bandwidth.service';
import { PingService } from './ping.service';
import { PrecallQualityResult } from '../../models/app/precall/precall-quality-result';
import { catchError, last, map, Observable, of, switchMap, tap, timeout } from 'rxjs';
import { VideoSessionService } from './video-session.service';

@Injectable({
  providedIn: 'root',
})
export class QualityTestService {
  readonly store = inject(QualityTestStore);
  readonly bandwidthService = inject(BandwidthService);
  readonly pingService = inject(PingService);
  readonly videoSessionService = inject(VideoSessionService);

  run(): Observable<PrecallQualityResult> {
    console.log('Starting quality test');
    const result = this.init();

    return of(result).pipe(
      map(this.onStart.bind(this)),
      switchMap(() =>
        this.pingService.ping(250, 20).pipe(
          map((ping) => ({ ping }) as PrecallQualityResult),
          tap(this.onUpdate.bind(this)),
          last(),
        ),
      ),
      switchMap((result) =>
        this.bandwidthService.testDownload(5).pipe(
          map((download) => ({ ...result, download }) as PrecallQualityResult),
          tap(this.onUpdate.bind(this)),
          last(),
        ),
      ),
      switchMap((result) =>
        this.bandwidthService.testUpload(5).pipe(
          map((upload) => ({ ...result, upload }) as PrecallQualityResult),
          tap(this.onUpdate.bind(this)),
          last(),
        ),
      ),
      switchMap((result) =>
        this.videoSessionService.qualityTest(false).pipe(
          // Second safety net: errors if qualityTest() hangs beyond its internal safety timeout.
          timeout(18_000),
          map((quality) => ({ ...result, audioBitrate: quality }) as PrecallQualityResult),
          tap(this.onUpdate.bind(this)),
          last(),
        ),
      ),
      tap(this.onSuccess.bind(this)),
      catchError(this.onFailed.bind(this)),
    );
  }

  init(): PrecallQualityResult {
    const result: PrecallQualityResult = { status: 'not_started' };
    this.store.update(result);
    return result;
  }

  protected onStart(result: PrecallQualityResult): PrecallQualityResult {
    const updated: PrecallQualityResult = { ...result, status: 'in_progress' };
    this.store.update(updated);
    return updated;
  }

  protected onUpdate(result: PrecallQualityResult): void {
    this.store.update({ ...result, status: 'in_progress' });
  }

  protected onSuccess(result: PrecallQualityResult): void {
    this.store.update({ ...result, status: 'success' });
  }

  protected onFailed(): Observable<PrecallQualityResult> {
    const result = this.store.snapshot;
    // Preserve any partial bitrate data collected before the failure so the
    // chart can still render with whatever measurements were captured.
    const partialBitrate = this.videoSessionService.audioBitRateStore.snapshot;

    if (result) {
      const failed: PrecallQualityResult = {
        ...result,
        status: 'failed',
        audioBitrate: result.audioBitrate ?? partialBitrate,
      };
      this.store.update(failed);
      return of(failed);
    }
    return of({ status: 'failed' });
  }
}
